import axios from 'axios';
import {
    entityCluster,
    keywordMonthRaw,
    entityRegistration,
    entityClusterBundle,
} from '../../../interfaces/generic-entity.interface';
import {
    governmentAnnouncement,
    IssueGazette,
    issueInfo,
    keywordMentions,
    Tweet,
} from '../interface/issue-entity.interface';
import {
    MPLinkFormatter,
    consultantLinkFormatter,
    lobbyFirmLinkFormatter,
    organizationLinkFormatter,
    sectorLinkFormatter,
    senatorLinkFormatter,
} from '../../navigation/routers/internal_routing';
import { bumperSuperList } from '../../../interfaces/bumper-types.interface';
import {
    committeeMeetingLinkFormatter,
    govAnnouncementLinkFormatter,
    twitterLinkFormatter,
} from '../../navigation/routers/external_routing';
import { recentUpdates } from '../../shared/interfaces/whats-new.interface';
import moment from 'moment';
import { RangedSummary } from 'features/shared/interfaces/text-summary.interface';
import { CommunicationService } from 'features/services/communications/CommunicationsService';
import { ConfigDescriptor } from 'features/config/feature-configs/ConfigDescriptor';
import { EntityType } from 'features/config/Entities';
import { CommunicationProcessor } from 'features/services/communications/processing/CommunicationProcessor';
import DPOHHelper from 'features/dpoh-dashboards/DPOHHelper';

// Base URL for accessing the committee API
const baseURL = process.env.REACT_APP_API_BASE_URL;

const service = new CommunicationService();
const issueEntityDataConfig = ConfigDescriptor.getEntityConfig(EntityType.Issues).entityDataConfig;

// Fetch an overview of a given issue
const fetchUpdates = async (code: string, date: string) => {
    const response = await axios.get(`${baseURL}/issue/${code}/updates?date=${date}`);
    const data: recentUpdates = response.data;
    return data;
};

// Fetch Issue summary
const fetchIssueSummary = async (code: string, date: string): Promise<RangedSummary> => {
    const summary_response = await axios.get(`${baseURL}/issue/${code}/summary?date=${date}`);
    const summary_data: RangedSummary = summary_response.data;

    return summary_data;
};

// Fetch relevant government announcements for a given issue
const fetchGovernmentAnnouncements = async (code: string, date: string) => {
    const response = await axios.get(`${baseURL}/issue/${code}/announcements?date=${date}`);
    const data: governmentAnnouncement[] = response.data.announcements.map(
        (entry: governmentAnnouncement) => {
            return {
                ...entry,
                title: govAnnouncementLinkFormatter(entry.link, entry.title),
            };
        }
    );
    return data;
};

// Fetch an overview of a given issue
const fetchKeywordMentions = async (code: string, date: string) => {
    const mentionsResponse = await axios.get(
        `${baseURL}/issue/${code}/keywordsSentenceCounts?date=${date}`
    );
    const mentionsData: keywordMentions[] = mentionsResponse.data.issueKeywords;
    return mentionsData;
};

// Fetch info for a given issue
const fetchInfo = async (code: string) => {
    const issueInfoResponse = await axios.get(`${baseURL}/issue/${code}/info`);
    const issueInfoData: issueInfo = issueInfoResponse.data;
    return issueInfoData;
};

// Fetch subject matter details summary for a given issue
const fetchSubjectMatterDetails = async (code: string, date: string): Promise<RangedSummary> => {
    const subjectMatterResponse = await axios.get(
        `${baseURL}/issue/${code}/subject-matter-summary?date=${date}`
    );
    const subjectMatterData: RangedSummary = subjectMatterResponse.data;
    return subjectMatterData;
};

// Fetch an overview of tweets for a given issue
const fetchSocialOverview = async (
    code: string,
    date: string,
    isSenatorTweets: boolean = false
) => {
    const overviewResponse = await axios.get(
        `${baseURL}/issue/${code}/social-overview?date=${date}&isSenatorTweets=${isSenatorTweets}`
    );
    const overviewData: any[] = overviewResponse.data.issueSocial;
    return overviewData;
};

// Fetch meeting sentences from recent meetings
const fetchSentences = async (code: string, date: string): Promise<bumperSuperList[]> => {
    const endDate = date;
    const startDate = moment(date).subtract(6, 'months').format('YYYY-MM-DD');
    const sentence_response = await axios({
        method: 'get',
        url: `${baseURL}/issue/${code}/sentences?limit=6&date=${date}`,
        headers: { dateStart: startDate, dateEnd: endDate },
    });
    const sentence_data: any[] = sentence_response.data;

    const sentence_list: any[] = sentence_data.map((entry: keywordMonthRaw) => {
        return {
            ...entry,
            title:
                entry.meetingNo &&
                entry.parliament &&
                entry.session &&
                entry.committee &&
                entry.committee !== 'HANS'
                    ? committeeMeetingLinkFormatter(
                          entry.committee,
                          entry.parliament,
                          entry.session,
                          entry.meetingNo,
                          entry.title
                              ? `${entry.committee} (Meeting ${entry.meetingNo} - ${entry.date}): ${entry.title} `
                              : `${entry.committee}: ${entry.date} - Meeting ${entry.meetingNo}`
                      )
                    : entry.title && entry.meetingNo && entry.date
                    ? `${entry.committee}: ${entry.title} (Meeting ${entry.meetingNo} - ${entry.date})`
                    : entry.meetingNo
                    ? `${entry.committee}: ${entry.date} - Meeting ${entry.meetingNo}`
                    : entry.date
                    ? `${entry.committee}: ${entry.date}`
                    : '',
            lists: entry.datasets,
        };
    });

    return sentence_list;
};

// Fetch recent tweets in a given issue
const fetchTweets = async (code: string, date: string) => {
    const response = await axios.get(`${baseURL}/issue/${code}/tweets?date=${date}`);
    const data: any[] = response.data;
    const converted_data: Tweet[] = data.map((entry) => {
        const link =
            entry.is_mp && entry.name
                ? MPLinkFormatter(entry.name)
                : entry.id
                ? senatorLinkFormatter(entry.id, entry.name)
                : entry.name;
        return {
            ...entry,
            name: link,
            tweet: twitterLinkFormatter(entry),
        };
    });
    return converted_data;
};

// Fetch recent lobbying communications in a given issue
const fetchRecentLobbyingActivity = async (code: string, date: string) => {
    const communicationData = await service.fetchEntityCommunications(
        code,
        moment(date)
            .subtract(issueEntityDataConfig.constants.communicationsLimitMonths, 'months')
            .format('YYYY-MM-DD'),
        date
    );

    let lobbyingActivityWithLinks =
        CommunicationProcessor.formatEntityCommunicationLinks(communicationData);

    let lobbyingActivityWithCorrelations =
        CommunicationProcessor.mapCommunicationTwoWayCorrelations(
            date,
            lobbyingActivityWithLinks,
            12,
            EntityType.Organization,
            EntityType.DPOH
        );

    lobbyingActivityWithCorrelations = CommunicationProcessor.filterCommunicationsByMonths(
        lobbyingActivityWithCorrelations,
        date,
        issueEntityDataConfig.constants.lobbyingActivityPeriodMonths
    );

    const orgActivity = CommunicationProcessor.mapCommunicationActivity(
        date,
        EntityType.Organization,
        lobbyingActivityWithLinks,
        12
    );

    const dpohActivity = CommunicationProcessor.mapCommunicationActivity(
        date,
        EntityType.DPOH,
        lobbyingActivityWithLinks,
        12
    );

    const mpActivity = dpohActivity.filter(
        (entry) => entry.title && DPOHHelper.getIsMp(entry.title)
    );

    const allSectorActivity = CommunicationProcessor.getCommunicationMonthlyAggregates(
        date,
        communicationData,
        12,
        EntityType.Sector
    );

    const topSectorActivity = CommunicationProcessor.getTopCommunicatingEntities(
        date,
        communicationData,
        EntityType.Sector,
        12,
        3
    );

    const communicationPackage = {
        lobbyingActivity: lobbyingActivityWithCorrelations,
        allSectorActivity: allSectorActivity,
        topThreeSectorActivity: topSectorActivity,
        orgActivity: orgActivity,
        dpohActivity: dpohActivity,
        mpActivity: mpActivity,
    };

    return communicationPackage;
};

const fetchProcessedCommunications = async (issueId: string, date: string) => {
    const dateStart = moment(date)
        .subtract(issueEntityDataConfig.constants.communicationsLimitMonths, 'months')
        .format('YYYY-MM-DD');
    const dateEnd = date;
    const processedCommunicationsData = await service.fetchProcessedEntityCommunications(
        {
            processSteps: [
                {
                    processingStepId: 'topEntitiesInstitution',
                    processingMethodId: 'topEntities',
                    processingParams: {
                        targetEntityType: 'institution',
                        dateStart: dateStart,
                        dateEnd: dateEnd,
                        N: 6,
                    },
                },
                {
                    processingStepId: 'topEntitiesSector',
                    processingMethodId: 'topEntities',
                    processingParams: {
                        targetEntityType: 'sector',
                        dateStart: dateStart,
                        dateEnd: dateEnd,
                        N: 6,
                    },
                },
                {
                    processingStepId: 'topEntitiesFirm',
                    processingMethodId: 'topEntities',
                    processingParams: {
                        targetEntityType: 'firm',
                        dateStart: dateStart,
                        dateEnd: dateEnd,
                        N: 6,
                    },
                },
            ],
        },
        issueId,
        dateStart,
        dateEnd
    );

    const processedCommunicationsResult = {
        topSectors:
            processedCommunicationsData.processedCommunications.find(
                (step) => step.processingStepId === 'topEntitiesSector'
            )?.result || [],
        topInstitutions:
            processedCommunicationsData.processedCommunications.find(
                (step) => step.processingStepId === 'topEntitiesInstitution'
            )?.result || [],
        topFirms:
            processedCommunicationsData.processedCommunications.find(
                (step) => step.processingStepId === 'topEntitiesFirm'
            )?.result || [],
    };

    return processedCommunicationsResult;
};

// Fetch recent registrations in a given issue
const fetchRecentRegistrations = async (
    code: string,
    date: string
): Promise<entityRegistration[]> => {
    const response = await axios.get(`${baseURL}/issue/${code}/recentregistrations?date=${date}`);
    const data: entityRegistration[] = response.data;
    const converted_data: entityRegistration[] = data.map((entry) => {
        return {
            ...entry,
            organization:
                entry.sector_id && entry.sector
                    ? organizationLinkFormatter(entry.sector_id, entry.sector, entry.client)
                    : entry.client,
            sector:
                entry.sector_id && entry.sector
                    ? sectorLinkFormatter(entry.sector_id, entry.sector)
                    : entry.sector,
            lobby_firm:
                entry.lobby_firm === 'In-House'
                    ? 'In-House'
                    : lobbyFirmLinkFormatter(entry.lobby_firm),
            consultant:
                entry.consultant === 'In-House'
                    ? 'In-House'
                    : entry.lobby_firm === 'In-House'
                    ? entry.consultant
                    : consultantLinkFormatter(entry.lobby_firm, entry.consultant),
            link: `https://lobbycanada.gc.ca/app/secure/ocl/lrs/do/vwRg?cno=${entry.corp}&regId=${entry.registration}#regStart`,
            dashboard: `link:/registrations/${entry.registration}`,
        };
    });
    return converted_data;
};

// Fetch recent deregistrations in a given issue
const fetchRecentDeregistrations = async (
    code: string,
    date: string
): Promise<entityRegistration[]> => {
    const response = await axios.get(`${baseURL}/issue/${code}/recentderegistrations?date=${date}`);
    const data: entityRegistration[] = response.data;
    const converted_data: entityRegistration[] = data.map((entry) => {
        return {
            ...entry,
            organization:
                entry.sector_id && entry.sector
                    ? organizationLinkFormatter(entry.sector_id, entry.sector, entry.client)
                    : entry.client,
            sector:
                entry.sector_id && entry.sector
                    ? sectorLinkFormatter(entry.sector_id, entry.sector)
                    : entry.sector,
            lobby_firm:
                entry.lobby_firm === 'In-House'
                    ? 'In-House'
                    : lobbyFirmLinkFormatter(entry.lobby_firm),
            consultant:
                entry.consultant === 'In-House'
                    ? 'In-House'
                    : entry.lobby_firm === 'In-House'
                    ? entry.consultant
                    : consultantLinkFormatter(entry.lobby_firm, entry.consultant),
            link: `https://lobbycanada.gc.ca/app/secure/ocl/lrs/do/vwRg?regId=${entry.registration}&cno=${entry.corp}#regStart`,
            dashboard: `link:/registrations/${entry.registration}`,
        };
    });
    return converted_data;
};

const fetchClusterBundle = async (code: number, date: string): Promise<entityClusterBundle> => {
    const endDate = date;
    const startDate = moment(date).subtract(12, 'months').format('YYYY-MM-DD');

    const cluster_response = await axios({
        method: 'post',
        url: `${baseURL}/communications/cluster`,
        headers: { dateStart: startDate, dateEnd: endDate, issueId: code, moversLimit: 25 },
        data: {
            clusters: {
                dpoh: true,
                institution: true,
                organization: true,
                sector: true,
                firm: true,
                subject: true,
            },
        },
    });

    const cluster_data: entityClusterBundle = cluster_response.data;
    return cluster_data;
};

// Fetch an issue data cluster of the given issue code, under a specific "type"
const fetchCluster = async (
    code: string,
    type:
        | 'organizations'
        | 'institutions'
        | 'lobbyfirms'
        | 'dpoh'
        | 'subject'
        | 'sectors'
        | 'mentions',
    date: string
): Promise<entityCluster> => {
    const cluster_response = await axios.get(
        `${baseURL}/issue/${code}/${type === 'mentions' ? '' : 'cluster/'}${type}?date=${date}`
    );
    const cluster_data: entityCluster = cluster_response.data;
    return cluster_data;
};

const fetchGazette = async (code: number, date: string): Promise<IssueGazette> => {
    const gazetteOneResponse = await axios.get(
        `${baseURL}/issue/${code}/gazette/part1?date=${date}`
    );
    const gazetteTwoResponse = await axios.get(
        `${baseURL}/issue/${code}/gazette/part2?date=${date}`
    );

    return {
        part_one: gazetteOneResponse.data.gazettes.map((entry: any) => {
            return {
                ...entry,
                sentence: `URL#${entry.sentence}#${entry.link}`,
            };
        }),
        part_two: gazetteTwoResponse.data.gazettes.map((entry: any) => {
            return {
                ...entry,
                sentence: `URL#${entry.sentence}#${entry.link}`,
            };
        }),
    };
};

const createOrUpdateIssue = async (
    issueName: string,
    issueKeywords: string[],
    issueId: number | null
) => {
    return await axios.post(`${baseURL}/issue/create`, {
        issueName,
        issueKeywords,
        issueId,
    });
};

const deleteIssue = async (issueId: number) => {
    await axios.delete(`${baseURL}/issue/delete/${issueId}`);
};

export const issueAPI = {
    fetchUpdates,
    fetchIssueSummary,
    fetchGovernmentAnnouncements,
    fetchTweets,
    fetchKeywordMentions,
    fetchInfo,
    fetchSubjectMatterDetails,
    fetchSocialOverview,
    fetchSentences,
    fetchProcessedCommunications,
    fetchRecentLobbyingActivity,
    fetchRecentRegistrations,
    fetchRecentDeregistrations,
    fetchCluster,
    fetchClusterBundle,
    fetchGazette,
    createOrUpdateIssue,
    deleteIssue,
};
